import axios from 'axios';

// Determine the API URL based on environment
// In development, use Vite proxy (empty string = same origin)
// In production, use the configured production API URL
const isDevelopment = import.meta.env.MODE === 'development';
export const API_URL = isDevelopment
  ? '' // Use Vite proxy for same-origin requests
  : (import.meta.env.VITE_PRODUCTION_API_URL || 'https://portal2-server.myfmsweb.com');

// Export API endpoints
export const API_ENDPOINTS = {
  login: '/api/login',
  checkAuth: '/api/check-auth',
  profile: '/api/profile',
  inventory: {
    bySku: '/api/inventory/sku',
    byLot: '/api/inventory/lots',
    totals: '/api/inventory/lots/totals',
    temperature: (id) => `/api/inventory/temperature/${id}`,
    catalog: '/api/inventory/catalog',
    catalogMetrics: '/api/inventory/catalog/metrics',
    catalogHistory: (id) => `/api/inventory/catalog/${id}/history`,
    catalogRoLevel: (id) => `/api/inventory/catalog/${id}/ro-level`,
  },
  orders: {
    list: '/api/orders',
    details: (id) => `/api/orders/${id}`,
    shippingRate: (id) => `/api/orders/${id}/shipping-rate`
  },
  extOrders: {
    list: '/api/ext-orders',
    details: (id) => `/api/ext-orders/${id}`,
    approve: (id) => `/api/ext-orders/${id}/approve`,
    reject: (id) => `/api/ext-orders/${id}/reject`,
    items: {
      approve: (id) => `/api/ext-orders/items/${id}/approve`,
      reject: (id) => `/api/ext-orders/items/${id}/reject`
    }
  },
  shipping: '/api/shipping',
  customers: '/api/customers',
  invoices: {
    list: '/api/invoices',
    details: '/api/invoices/details',
    client: '/api/invoices/client',
    documents: '/api/invoices/documents'
  },
  purchaseOrders: {
    list: '/api/purchase-orders',
    export: '/api/purchase-orders/export',
    detail: (id) => `/api/purchase-orders/${id}`,
  },
  returns: {
    list: '/api/returns',
    export: '/api/returns/export',
    detail: (id) => `/api/returns/${id}`,
    notes: (id) => `/api/returns/${id}/notes`,
  },
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // required for httpOnly cookie to be sent cross-origin
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

let hasRedirected = false;

// Add response interceptor for JWT error handling
api.interceptors.response.use(
  response => response,
  async error => {
    const requestUrl = error.config?.url || '';
    const isLoginRequest = requestUrl === API_ENDPOINTS.login || requestUrl.includes('/api/login');
    // check-auth 401 is handled by AuthContext — don't intercept it here
    const isCheckAuthRequest = requestUrl === API_ENDPOINTS.checkAuth || requestUrl.includes('/api/check-auth');

    if (error.response?.status === 401 && !isLoginRequest && !isCheckAuthRequest) {
      // Cookie is cleared server-side via /api/logout — just clean up local state
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('permissions');
      sessionStorage.removeItem('shipEngineAPI');
      sessionStorage.removeItem('cart');

      if (!hasRedirected && window.location.pathname !== '/login') {
        hasRedirected = true;
        setTimeout(() => {
          hasRedirected = false;
          window.location.href = '/login';
        }, 100);
      }
    }

    return Promise.reject(error);
  }
);

// Inventory API functions
export const getInventoryByLot = async (page = 1, limit = 50, search = '', sortKey = null, sortDirection = 'ascending', loadAll = false) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (search) {
      params.append('search', search);
    }

    if (sortKey) {
      params.append('sortKey', sortKey);
      params.append('sortDirection', sortDirection);
    }

    if (loadAll) {
      params.append('loadAll', 'true');
    }

    const response = await api.get(`${API_ENDPOINTS.inventory.byLot}?${params}`);
    
    if (!response.data || !response.data.items || !response.data.pagination) {
      throw new Error('Invalid response format from server');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching inventory by lot:', error);
    return { items: [], pagination: { total: 0, page: 1, pageSize: limit, totalPages: 0 } };
  }
};

export const exportInventoryByLot = async (search = '') => {
  try {
    const response = await api.get('/api/inventory/export', {
      params: search ? { search } : {},
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error exporting inventory:', error);
    throw error;
  }
};

export const getInventoryTotals = async (search = '') => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append('search', search);
    }
    const response = await api.get(`${API_ENDPOINTS.inventory.totals}${search ? `?${params}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory totals:', error);
    return { lotCount: 0, uoh: 0, available: 0, tap: 0 };
  }
};

export const getInventoryBySKU = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.inventory.bySku);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory by SKU:', error);
    throw error;
  }
};

// Fast catalog list (core columns only, no metric subqueries)
export const getCatalogItems = async (page = 1, limit = 50, search = '') => {
  try {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    const response = await api.get(`${API_ENDPOINTS.inventory.catalog}?${params}`);
    if (!response.data?.items || !response.data?.pagination) {
      throw new Error('Invalid response format from server');
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching catalog items:', error);
    return { items: [], pagination: { total: 0, totalPages: 0, currentPage: page, hasMore: false, limit } };
  }
};

// Lazy-loaded metrics for a batch of catalog IDs
// idList: number[]
export const getCatalogMetrics = async (idList = []) => {
  if (!idList.length) return { metrics: {} };
  try {
    const params = new URLSearchParams({ ids: idList.join(',') });
    const response = await api.get(`${API_ENDPOINTS.inventory.catalogMetrics}?${params}`);
    return response.data || { metrics: {} };
  } catch (error) {
    console.error('Error fetching catalog metrics:', error);
    return { metrics: {} };
  }
};

// Full history for a single catalog item (for detail modal)
export const getCatalogItemHistory = async (idCatalog) => {
  try {
    const response = await api.get(API_ENDPOINTS.inventory.catalogHistory(idCatalog));
    return response.data;
  } catch (error) {
    console.error('Error fetching catalog item history:', error);
    throw error;
  }
};

// Save a user-set reorder point for a catalog item (0 = clear / revert to recommended)
export const updateCatalogRoLevel = async (idCatalog, roLevel) => {
  const response = await api.patch(
    API_ENDPOINTS.inventory.catalogRoLevel(idCatalog),
    { roLevel }
  );
  return response.data;
};

export const getTempHistory = async (idLevels, { range, startDate, endDate } = {}) => {
  try {
    const params = new URLSearchParams();
    if (range) params.append('range', range);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`${API_ENDPOINTS.inventory.temperature(idLevels)}?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching temperature history:', error);
    throw error;
  }
};

// Orders API functions
export const getOrders = async (startDate, endDate) => {
  try {
    const response = await api.get(API_ENDPOINTS.orders.list, {
      params: { startDate, endDate }
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const getOrderDetails = async (orderId) => {
  try {
    const response = await api.get(API_ENDPOINTS.orders.details(orderId));
    return response.data;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

export const updateOrderShippingRate = async (orderId, shippingData) => {
  try {
    const response = await api.put(API_ENDPOINTS.orders.shippingRate(orderId), shippingData);
    return response.data;
  } catch (error) {
    console.error('Error updating shipping rate:', error);
    throw error;
  }
};

// Profile API functions
export const getProfile = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.profile);
    return response.data;
  } catch (error) {
    console.error('Error fetching profile:', error);
    throw error;
  }
};

export const updateProfile = async (profileData) => {
  try {
    const response = await api.put(API_ENDPOINTS.profile, profileData);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

// External Orders API functions
export const getExtOrders = async (startDate, endDate, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters.status) params.append('status', filters.status);
    if (filters.approvalStatus) params.append('approvalStatus', filters.approvalStatus);
    
    const response = await api.get(`${API_ENDPOINTS.extOrders.list}?${params}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching external orders:', error);
    throw error;
  }
};

export const getExtOrderDetails = async (orderId) => {
  try {
    const response = await api.get(API_ENDPOINTS.extOrders.details(orderId));
    return response.data;
  } catch (error) {
    console.error('Error fetching external order details:', error);
    throw error;
  }
};

export const approveExtOrder = async (orderId) => {
  try {
    const response = await api.post(API_ENDPOINTS.extOrders.approve(orderId));
    return response.data;
  } catch (error) {
    console.error('Error approving external order:', error);
    throw error;
  }
};

export const rejectExtOrder = async (orderId, reason) => {
  try {
    const response = await api.post(API_ENDPOINTS.extOrders.reject(orderId), { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting external order:', error);
    throw error;
  }
};

export const approveExtOrderItem = async (itemId, quantity) => {
  try {
    const response = await api.post(API_ENDPOINTS.extOrders.items.approve(itemId), { 
      approved_quantity: quantity 
    });
    return response.data;
  } catch (error) {
    console.error('Error approving external order item:', error);
    throw error;
  }
};

export const rejectExtOrderItem = async (itemId, reason) => {
  try {
    const response = await api.post(API_ENDPOINTS.extOrders.items.reject(itemId), { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting external order item:', error);
    throw error;
  }
};

// Inventory Approval API functions
export const getPendingApprovalsCount = async () => {
  try {
    const response = await api.get('/api/inventory/pending-approvals/count');
    return response.data.count || 0;
  } catch (error) {
    console.error('Error fetching pending approvals count:', error);
    return 0;
  }
};

export const getPendingApprovals = async () => {
  try {
    const response = await api.get('/api/inventory/pending-approvals');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    throw error;
  }
};

export const approveOrder = async (orderId) => {
  try {
    const response = await api.post(`/api/inventory/approve-order/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error approving order:', error);
    throw error;
  }
};

export const rejectOrder = async (orderId, reason) => {
  try {
    const response = await api.post(`/api/inventory/reject-order/${orderId}`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting order:', error);
    throw error;
  }
};

// Purchase Orders API functions
export const getPurchaseOrders = async (params = {}) => {
  try {
    const response = await api.get(API_ENDPOINTS.purchaseOrders.list, { params });
    return response.data; // { items, pagination }
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    throw error;
  }
};

export const exportPurchaseOrders = async (params = {}) => {
  try {
    const response = await api.get(API_ENDPOINTS.purchaseOrders.export, {
      params,
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error exporting purchase orders:', error);
    throw error;
  }
};

export const getPurchaseOrderDetail = async (id) => {
  try {
    const response = await api.get(API_ENDPOINTS.purchaseOrders.detail(id));
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase order detail:', error);
    throw error;
  }
};

// Returns API functions
export const getReturns = async (params = {}) => {
  try {
    const response = await api.get(API_ENDPOINTS.returns.list, { params });
    return response.data; // { items, pagination }
  } catch (error) {
    console.error('Error fetching returns:', error);
    throw error;
  }
};

export const exportReturns = async (params = {}) => {
  try {
    const response = await api.get(API_ENDPOINTS.returns.export, {
      params,
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error exporting returns:', error);
    throw error;
  }
};

export const getReturnDetail = async (id) => {
  try {
    const response = await api.get(API_ENDPOINTS.returns.detail(id));
    return response.data;
  } catch (error) {
    console.error('Error fetching return detail:', error);
    throw error;
  }
};

export const getReturnNotes = async (id) => {
  try {
    const response = await api.get(API_ENDPOINTS.returns.notes(id));
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching return notes:', error);
    throw error;
  }
};

export default api;